// Import Core
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import React from 'react';
import { useState } from 'react';
import { FaMinus, FaPlus } from 'react-icons/fa';
import styled from 'styled-components';
import { usePlaceholderImage } from '../../utils/useRandomPlaceholderImage';
import { Link } from 'gatsby';
import { getSlug } from '../../utils/getSlug';
import Button from '../ui/buttons/Button';
import { toSlug } from '../../utils/search/toSlug';

const AccordionDetailedStyling = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;
  padding: 64px;
  @media (max-width: 768px) {
    padding: 32px 20px;
  }
  .section-title {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    @media (max-width: 768px) {
      flex-direction: column;
      gap: 24px;
    }
    .btn-group {
      display: flex;
      align-items: flex-start;
      gap: 24px;
      @media (max-width: 768px) {
        flex-flow: wrap;
        button {
          width: 100%;
        }
      }
    }
  }
  .accordion-items {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;

    .accordion-item {
      display: flex;
      padding: 20px 0px;
      align-items: center;
      gap: 12px;
      width: 100%;
      flex-direction: column;
      border-bottom: 1px solid var(--neutral-25);
      &:first-child {
        border-top: 1px solid var(--neutral-25);
      }
      @media (max-width: 768px) {
        display: flex;
        padding: 16px 0px;
        align-items: center;
        gap: 12px;
      }
      .accordion-heading {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 24px;
        width: 100%;
        cursor: pointer;
        &.alignHeight{
          @media (min-width: 768px) {
            height: 100px;
          }

        }
        .accordion-title-img {
          width: 140px;
          height: 100px;
          transition: all 0.3 ease-in;
          @media (max-width: 768px) {
            display: none;
          }
        }
        .accordion-title {
          text-transform: uppercase;
          flex: 1 0 0;
          span {
            font-size: inherit;
            font-family: var(--font-family-heading)
          }
        }
      }
      .accordion-content {
        display: flex;
        padding-bottom: 0px;
        align-items: flex-start;
        gap: 32px;
        width: inherit;
        justify-content: space-between;
        @media (max-width: 768px) {
          flex-direction: column;
          gap: 20px;
        }
        .content {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 16px;
          @media (max-width: 768px) {
            gap: 12px;
          }
          .related {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
            .related-items {
              display: flex;
              height: 24px;
              align-items: flex-start;
              gap: 16px;
              width: 100%;
              @media (max-width: 768px) {
                justify-content: space-between;
              }
              h5 {
                color: var(--neutral-75);
              }
              .related-item {
                display: flex;
                padding-right: 0px;
                align-items: flex-start;
                gap: 4px 8px;
                flex-wrap: wrap;
                .item {
                  color: var(--red);
                  text-decoration: underline;
                }
              }
            }
          }
        }
        .item-image {
          width: 45%;
          max-width: 420px;
          max-height: 250px;
          flex: 0 0 40%;
          @media (max-width: 768px) {
            max-height: 200px;
            height: 200px;
            width: 100%;
            margin: 0 auto;
          }
          .gatsby-image-wrapper, img {
            width: 100%;
            height: 100%;
            max-height: inherit;
            max-width: inherit;
            object-fit: cover;
          }
        }
      }

      &.active {
        padding-bottom: 40px;
        .accordion-heading {
          .accordion-title-img {
            display: none;
          }
          .accordion-title {
            span {
              color: var(--red);
            }
          }
        }
      }
    }
  }
`;

const AccordionDetailed = ({
  // title,
  // content,
  // isActiveNavItem,
  // index,
  // activeIndex,
  // handleClick,
  addToAnchor,
  title,
  items
}) => {

  const { randomPlaceholderImage } = usePlaceholderImage()


  const [isActiveNavItem, setIsActiveNavItem] = useState(false);
  const [activeIndex, setActiveIndex] = useState(null);

  const handleClick = (index) => {
    if (index === 'all') {
      setActiveIndex('all');
      setIsActiveNavItem(true);
    } else if (index === 'none') {
      setActiveIndex(null);
      setIsActiveNavItem(false);
    } else {
      if (activeIndex !== 'all') {
        if (isActiveNavItem) {
          if (activeIndex === index) {
            setActiveIndex(null);
            setIsActiveNavItem(false);
          } else {
            setActiveIndex(index);
          }
        } else {
          if (activeIndex === null) {
            setActiveIndex(index);
            setIsActiveNavItem(true);
          }
        }
      } else {
        // Currently If 'All' are expanded need a reset and close all (needs a fix to work properly)
        setActiveIndex(null);
        setIsActiveNavItem(false);
      }
    }
  };

  //boolean to store if accordion items without image should stretch
  const alignHeight = Boolean(items?.filter(item => item.image).length)

  return (
    <AccordionDetailedStyling id={addToAnchor ? `anchor_${toSlug(title)}` : ''} className={`${addToAnchor && 'anchor-link'}`}>
      <div className="section-title">
        <h3>{title}</h3>
        <div className="btn-group">
          <Button
            size='md'
            btnStyle='outline'
            className=''
            value='Close All'
            clickHandler={() => handleClick('none')}
            isDisabled={false}
          />
          <Button
            size='md'
            btnStyle='outline'
            className=''
            value='Expand All'
            clickHandler={() => handleClick('all')}
            isDisabled={false}
          />
        </div>
      </div>
      <div className="accordion-items">
        {items && items.map((item, index) => {
          const { day, title, content, image, includedCities, includedHotels } = item;
          return (
            <div className={`accordion-item ${isActiveNavItem && 'collapsed'}${(index === activeIndex || activeIndex === 'all') ? ' active' : ''}`} key={`accordion-item-${index}`}>
              <div
                className={`accordion-heading ${alignHeight ? 'alignHeight' : ''}`}
                onClick={() => handleClick(index)}
              >
                {image && image.asset &&
                  <GatsbyImage image={getImage(image.asset)} alt={title} className="accordion-title-img" />
                }
                <h5 className='accordion-title'>{day} - <span>{title}</span></h5>
                {(index === activeIndex || activeIndex === 'all') ? <FaMinus /> : <FaPlus />}
              </div>

              {(index === activeIndex || activeIndex === 'all') && (
                <div className='accordion-content'>
                  <div className="content">
                    <p className="large">
                      {content}
                    </p>

                    {(includedCities.length > 0 || includedHotels.length > 0) && (
                      <div className="related">
                        {includedCities.length > 0 && (
                          <div className="related-items">
                            <h5 className="alt bold">Places:</h5>
                            <div className="related-item">
                              {includedCities.map((city, index) => (
                                <Link key={index} to={getSlug(city)} className='item'>
                                  <p>{city.name}</p>
                                </Link>
                              ))}
                            </div>
                          </div>
                        )}
                        {includedHotels.length > 0 && (
                          <div className="related-items">
                            <h5 className="alt bold">Accommodation:</h5>
                            <div className="related-item">
                              {includedHotels.map((hotel, index) => (
                                <Link key={index} to={getSlug(hotel)} className='item'>
                                  <p>{hotel.title}</p>
                                </Link>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="item-image">
                    {(image && image.asset) && (
                      <GatsbyImage image={getImage(image.asset)} alt={title} className="accordion-item-img" />
                    )}
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </AccordionDetailedStyling>
  );
};

export default AccordionDetailed;
